<template>
  <section class="h-full">
    <div class="flex md:flex-row flex-col justify-between">
      <div class="h-full">
        <div class="flex items-center gap-x-2.5 cursor-pointer">
          <div class="flex items-center gap-x-1">
            <currency-icons :currency="getCurrencyCountry(selectedCurrency)" />
            <currency-icons :currency="getCurrencyCountry(toCurrency)" />
          </div>
          <div class="flex items-center">
            <p class="text-lg font-normal text-fg-default">
              {{ selectedCurrency }}/{{ toCurrency }}
            </p>
          </div>
        </div>
        <div class="md:mt-20 my-5 flex items-center gap-x-1">
          <p class="text-lg font-bold text-fg-default">
            {{
              parseMoneyAll(
                selectedExchangeRateDetails.exchange_rate,
                selectedCurrency
              )
            }}
          </p>
          <div>
            <div
              class="rounded-xl px-1.5 text-center text-xs text-error-emphasis font-medium bg-error-subtle py-1"
              :class="{ 'bg-green-6 text-green-5': !isNegativePercentageRate }"
            >
              {{ percentageChange || 0 }}%
            </div>
          </div>
        </div>
      </div>
      <!-- graph -->
      <div v-if="rateChange.length > 0">
        <apexchart
          ref="realtimeChart"
          type="area"
          height="200"
          :options="chartOptions"
          :series="series"
        >
        </apexchart>
      </div>
      <div v-else class="flex items-center justify-center md:mr-12">
        <div>
          <empty-state class="w-48" />
          <p class="text-sm text-fg-muted font-normal text-center">
            No rate change
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  GetCurrencyCountry,
  ParseMoneyAll,
  GetNetworkFullName
} from '@/utils/helpers.js'

export default {
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      series: [
        {
          name: 'Exchange Rate',
          data: []
        }
      ],
      chartOptions: {
        chart: {
          toolbar: {
            show: false
          },
          type: 'area',
          stacked: false,
          height: 150,
          zoom: {
            enabled: false
          }
        },
        title: {
          text: '',
          align: 'right'
        },
        colors: ['#1570F2'],
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth',
          width: [3]
        },
        xaxis: {
          type: 'categories',
          categories: [],
          show: true,
          lines: {
            show: true
          },
          labels: {
            offsetX: 2,
            hideOverlappingLabels: false,
            trim: false,
            rotate: 0,
            show: true,
            maxHeight: 50,
            style: {
              colors: [],
              fontSize: '9px',
              fontFamily: 'Plus Jakarta Sans',
              fontWeight: 600,
              cssClass: 'apexcharts-xaxis-label'
            },
            datetimeFormatter: {
              year: 'yyyy',
              month: "MMM 'yy",
              day: 'dd MMM',
              hour: 'HH:mm'
            }
          }
        },
        grid: {
          show: false,
          xaxis: {
            lines: {
              show: false
            }
          },
          colors: [this.$primaryColor],
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        yaxis: {
          opposite: true,
          show: false
        },
        legend: {
          horizontalAlign: 'right'
        }
      }
    }
  },
  components: {
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    EmptyState: () => import('@/assets/icons/EmptyState.vue')
  },
  methods: {
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    getNetworkFullName (val) {
      return GetNetworkFullName(val)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    setSeriesData () {
      this.series[0].data = this.rateChange.reverse().map((rate) => rate.rate)
    },
    setXaxisCategories () {
      const dates = this.rateChange.map((rate) =>
        this.$dayjs(rate.date).format('DD/MM')
      )
      const yMax = Math.max(...this.series[0].data)
      const yMin = Math.min(...this.series[0].data)
      this.chartOptions.yaxis.max = yMax
      this.chartOptions.yaxis.min = yMin - 5
      this.chartOptions = {
        ...this.chartOptions,
        ...{
          xaxis: {
            categories: dates,
            type: 'categories'
          }
        }
      }
    },
    plotAreaChart () {
      this.setSeriesData()
      this.setXaxisCategories()
    },
    updateSeriesLine () {
      this.$refs.realtimeChart.updateSeries(
        [
          {
            data: this.series[0].data
          }
        ],
        false,
        true
      )
    }
  },
  async mounted () {
    await this.plotAreaChart()
  },
  computed: {
    rateChange () {
      return this.$store.state.report.currencyUpdates.rateChange || []
    },
    currencyUpdates () {
      return this.$store.state.report.currencyUpdates
    },
    percentageChange () {
      return this.currencyUpdates.percentageChange.toFixed(2)
    },
    isNegativePercentageRate () {
      return this.currencyUpdates.percentageChange < 0
    },
    exchangeRateData () {
      return this.$store.state.auth.transferDetails
    },
    selectedCurrency () {
      return this.$store.state.report.accountDetailsCurrency
    },
    toCurrency () {
      return this.selectedCurrency === this.$CURRENCY.USD
        ? this.$CURRENCY.NGN
        : this.$CURRENCY.USD
    },
    selectedExchangeRateDetails () {
      let details = {}
      if (this.selectedCurrency !== this.$CURRENCY.USD) {
        details = this.exchangeRateData.find(
          (item) =>
            item.from === this.selectedCurrency &&
            item.to === this.$CURRENCY.USD
        )
      } else {
        details = this.exchangeRateData.find(
          (item) =>
            item.from === this.$CURRENCY.NGN && item.to === this.$CURRENCY.USD
        )
      }
      return details.exchange_rate ? details : {}
    }
  }
}
</script>

<style scoped></style>
